import { mcn } from "@/utils/mergeClassNames";
import type { GetServerSidePropsContext, NextPage } from "next";
import React, { PropsWithChildren } from "react";
import ObjectHeader from "@/components/ObjectHeader";
import {
  LearningOpportunitySection,
  Section,
  SectionContainer,
} from "@/components/Sections";
import {
  CompetenceBaseEx,
  CompetenceEx,
  getAllLearningOpportunitiesOnTopic,
  getRecommendationsGetStartedOnTopic,
  getRecommendationsMostPopularByTopic,
  getTopic,
  LightWeightLearningOpportunities,
} from "@/server/backend-api";
import Link from "next/link";
import { buildUrlWithQuery, getRedirectIfUrlMismatch } from "@/utils/url";
import { BrowseQueryParams } from "../../browse";
import { LearningOpportunityType } from "@/server/backend-api/generated";
import { withErrorReportingAndAuthorization } from "../../../middlewares/withErrorReportingAndAuthorisation";
import { Head } from "@/components/Head";
import { GetServerSidePropsResult } from "next/types";
import pluralize from "@/utils/pluralize";
import { Markdown } from "@/components/Markdown";
import { SkillTab } from "@/components/Sections/LearningOutcomeSections";
import { AnchorLinkItem } from "@/components/AnchorLinks/AnchorLinks";
import eciuTopic from "../../../public/images/topic-eciu.svg";
import Image from "next/image";
import config from "../../../config";

type PageProps = {
  topic: CompetenceEx;
  mostPopularLOs: LightWeightLearningOpportunities[];
  gettingStartedLOs: LightWeightLearningOpportunities[];
  mostRecentLOs: LightWeightLearningOpportunities[];
  challenges: LightWeightLearningOpportunities[];
  microModules: LightWeightLearningOpportunities[];
};

const getServerSidePropsImpl = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<PageProps>> => {
  const topicId = context.params?.id as string;
  const [
    topic,
    mostPopularLOs,
    gettingStartedLOs,
    mostRecentLOs,
    challenges,
    microModules,
  ] = await Promise.all([
    getTopic(topicId, context.req),
    getRecommendationsMostPopularByTopic(topicId, context.req),
    getRecommendationsGetStartedOnTopic(topicId, context.req),
    getAllLearningOpportunitiesOnTopic(
      topicId,
      undefined,
      undefined,
      true,
      context.req
    ),
    getAllLearningOpportunitiesOnTopic(
      topicId,
      undefined,
      LearningOpportunityType.Challenge,
      undefined,
      context.req
    ),
    getAllLearningOpportunitiesOnTopic(
      topicId,
      undefined,
      LearningOpportunityType.MicroModule,
      undefined,
      context.req
    ),
  ]);

  const redirect = getRedirectIfUrlMismatch(context, topic);
  if (redirect) {
    return redirect;
  }

  return {
    props: {
      topic,
      mostPopularLOs,
      gettingStartedLOs,
      mostRecentLOs,
      challenges,
      microModules,
    },
  };
};

export const getServerSideProps = withErrorReportingAndAuthorization(
  getServerSidePropsImpl
);

const CompetenceCountCard = (
  props: PropsWithChildren<{ className?: string; href: string }>
) => {
  return (
      <Link
        href={props.href}
        className={mcn("focus-visible:default-focus rounded-3", props.className)}
      >
        <li
          className={mcn(
            "h-40 w-full rounded-3 link",
            "bg-light-blue text-style-headline3",
            "flex items-center justify-center text-center list-none",
            props.className
          )}
        >
          {props.children}
        </li>
      </Link>
  );
};

const Page: NextPage<PageProps> = ({
  topic,
  mostPopularLOs,
  gettingStartedLOs,
  mostRecentLOs,
  challenges,
  microModules,
}) => {
  const browseTopicChallengesUrl = buildUrlWithQuery<BrowseQueryParams>(
    "/browse",
    {
      topicIds: topic.id,
      learningOppTypes: LearningOpportunityType.Challenge.toString(),
    }
  );
  const browseTopicMicroModulesUrl = buildUrlWithQuery<BrowseQueryParams>(
    "/browse",
    {
      topicIds: topic.id,
      learningOppTypes: LearningOpportunityType.MicroModule.toString(),
    }
  );

  const links: AnchorLinkItem[] = [
    {
      text: "About",
      id: "about",
    },
  ];
  if (mostPopularLOs.length > 0) {
    links.push({
      text: "Popular",
      id: "popular",
    });
  }
  links.push({
    text: "Subtopics",
    id: "subtopics",
  });

  const relatedLength =
    gettingStartedLOs.length +
    mostRecentLOs.length +
    challenges.length +
    microModules.length;

  if (relatedLength > 0) {
    links.push({
      text: "Related",
      id: "related",
    });
  }

  return (
    <>
      <Head title={topic.name} description={topic.description} />
      <ObjectHeader
        title={topic.name}
        subTitle={topic.subTitle}
        image={<TopicImage competence={topic} />}
        anchorLinksProps={{
          anchorLinks: links,
        }}
      />
      <Section
        id={"about"}
        isFirstSection
        fillScreenHeight={false}
        color="blue"
      >
        <SectionContainer grid className="!pt-2xl">
          <div className={"col-span-full laptop:col-span-8"}>
            <h2 className="text-style-headline2 text-light-yellow">About</h2>
            <div className="mt-4">
              <Markdown content={topic.description ?? ""} />
            </div>
          </div>

          <div
            className={mcn(
              "col-span-full",
              "grid gap-default",
              "grid-cols-1 tablet:grid-cols-2"
            )}
          >
            <CompetenceCountCard href={browseTopicChallengesUrl}>
              {topic.challengeCount}{" "}
              {pluralize("challenge", topic.challengeCount)}
            </CompetenceCountCard>
            <CompetenceCountCard
              href={browseTopicMicroModulesUrl}
              className="rounded-l-[96px]"
            >
              {topic.microModuleCount}{" "}
              {pluralize("micro-module", topic.microModuleCount)}
            </CompetenceCountCard>
          </div>
        </SectionContainer>
      </Section>
      <LearningOpportunitySection
        id={"popular"}
        color={"light-yellow"}
        data={[
          {
            heading: "Popular challenges and micro-modules",
            learningOpps: mostPopularLOs,
          },
        ]}
      />
      <Section id={"subtopics"} color={"blue"}>
        <SectionContainer grid gridNoYGap className="gap-y-8 laptop:gap-y-16">
          <div className={"col-span-full flex justify-center"}>
            <h2 className=" text-style-headline2 text-light-yellow">
              Subtopics
            </h2>
          </div>

          {topic.subTopics?.map((subTopic) => (
            <div
              id={subTopic.id}
              key={subTopic.id}
              className="col-span-full flex justify-center"
            >
              <div className="max-w-[1000px] w-full min-h-[400px] relative bg-light-blue py-6 laptop:py-24 px-6 laptop:px-24 grid grid-flow-col grid-cols-4 gap-y-4">
                <div className="col-span-full laptop:col-span-1 flex justify-center">
                  <div
                    className={
                      "laptop:absolute -left-20 top-16 w-44 h-44 laptop:w-[300px] laptop:h-[300px]"
                    }
                  >
                    <TopicImageBase competence={subTopic} size={300} />
                  </div>
                </div>
                <div className="col-span-full laptop:col-span-3 flex flex-col max-w-[800px]">
                  <h2 className={"text-style-headline3"}>{subTopic.name}</h2>
                  <p className="mt-1 w-full">{subTopic.description}</p>
                  {subTopic.skills !== null && subTopic.skills.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-style-subtitle1">ESCO skills</h3>
                      <div className="flex gap-2 flex-shrink-0 flex-wrap mt-2">
                        {subTopic.skills.map((skill) => (
                          <SkillTab skill={skill} key={skill.id} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </SectionContainer>
      </Section>
      <LearningOpportunitySection
        id="related"
        color="light-gray"
        data={[
          {
            heading: `Get started with ${topic.name}`,
            learningOpps: gettingStartedLOs,
          },
          {
            heading: "Latest opportunities",
            learningOpps: mostRecentLOs,
          },
          {
            heading: "Challenges within this topic",
            learningOpps: challenges,
          },
          {
            heading: "Micro-modules within this topic",
            learningOpps: microModules,
          },
        ]}
      />
    </>
  );
};

function TopicImageBase(props: {
  competence: CompetenceBaseEx;
  size?: number;
}) {
  const { competence, size } = props;
  const imageUri = competence.imageUri;
  if (imageUri === null) {
    return (
      <Image
        width={size}
        height={size}
        style={{
          objectFit: "contain",
        }}
        src={eciuTopic}
        alt={"Image for topic " + competence.name}
      />
    );
  }

  return (
    <Image
      width={280}
      height={280}
      style={{
        clipPath: BASE_CLIP_PATH,
        objectFit: "contain",
      }}
      loader={() => {
        return `${config.cdn.url}/${imageUri}`;
      }}
      src={eciuTopic}
      alt={competence.name ?? "ECIU Logo"}
    />
  );
}

const BASE_CLIP_PATH = "polygon(0 53%, 100% 0, 100% 47%, 0 100%)";

function TopicImage(props: { competence: CompetenceEx }) {
  const { competence } = props;

  return (
    <div
      className={"bg-yellow-100 px-2 py-2"}
      style={{
        clipPath: BASE_CLIP_PATH,
      }}
    >
      <TopicImageBase competence={competence} />
    </div>
  );
}

export default Page;
